import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Input, Table, Space, Button, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetQaqcPipeHeatno } from '../../hooks/genericHooks';
import type { QaqcPipeHeatno } from '../../types';

const { Text } = Typography;

export interface PipeHeatNoSelectProps {
  open: boolean;
  onCancel: () => void;
  onOk: (heatNo: string, row?: QaqcPipeHeatno) => void;
  title?: string;
}

const PipeHeatNoSelect: React.FC<PipeHeatNoSelectProps> = ({ open, onCancel, onOk, title }) => {
  const { data, isLoading } = useGetQaqcPipeHeatno();
  const rows: QaqcPipeHeatno[] = (data?.data as any) || [];

  const [query, setQuery] = useState('');
  const [heatQuery, setHeatQuery] = useState('');
  const [selectedRow, setSelectedRow] = useState<QaqcPipeHeatno | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const filtered = useMemo(() => {
    const qComp = query.trim().toLowerCase();
    const qHeat = heatQuery.trim().toLowerCase();
    const compTokens = qComp ? qComp.split(/\s+/).filter(Boolean) : [];
    const heatTokens = qHeat ? qHeat.split(/\s+/).filter(Boolean) : [];
    return rows.filter((r) => {
      const component = String(r.component_name || '').toLowerCase();
      const heat = String(r.heat_no || '').toLowerCase();
      const compOk = !compTokens.length || compTokens.every(t => component.includes(t));
      const heatOk = !heatTokens.length || heatTokens.every(t => heat.includes(t));
      return compOk && heatOk;
    });
  }, [rows, query, heatQuery]);

  useEffect(() => { setPage(1); }, [query, heatQuery]);

  const columns: ColumnsType<QaqcPipeHeatno> = [
    { title: 'Component', dataIndex: 'component_name', key: 'component_name', width: 60 },
    { title: 'Manufacturer', dataIndex: 'manufacture', key: 'manufacture', width: 80 },
    { title: 'PMI', dataIndex: 'pmi_ref_no', key: 'pmi_ref_no', width: 40 },
    { title: 'Heat No', dataIndex: 'heat_no', key: 'heat_no', width: 60 },
  ];

  return (
    <Modal
      open={open}
      title={title || 'Select Heat Number'}
      onCancel={onCancel}
      footer={null}
      width={400}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }} size={8}>
        <style>{`
          .heatno-table .ant-table-tbody > tr > td,
          .heatno-table .ant-table-thead > tr > th {
            padding-top: 4px !important;
            padding-bottom: 4px !important;
          }
        `}</style>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            placeholder="Filter component"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            allowClear
          />
          <Input
            placeholder="Filter heat no"
            value={heatQuery}
            onChange={(e) => setHeatQuery(e.target.value)}
            allowClear
          />
        </Space.Compact>
        <Table
          className="heatno-table"
          size="small"
          rowKey={(r) => String(r.id)}
          columns={columns as any}
          dataSource={filtered}
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10','20','50','100'],
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          onRow={(record) => ({
            onClick: () => setSelectedRow(record),
            onDoubleClick: () => onOk(String(record.heat_no || ''), record),
          })}
          rowClassName={(r) => (selectedRow && selectedRow.id === r.id ? 'ant-table-row-selected' : '')}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text type="secondary">{selectedRow ? `Selected: ${selectedRow.component_name || ''} → ${selectedRow.heat_no || ''}` : 'No selection'}</Text>
          <Space>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              disabled={!selectedRow || !selectedRow.heat_no}
              onClick={() => {
                if (selectedRow?.heat_no) onOk(String(selectedRow.heat_no), selectedRow);
              }}
            >
              Use Heat No
            </Button>
          </Space>
        </div>
      </Space>
    </Modal>
  );
};

export default PipeHeatNoSelect;
